import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../styles/theme';
import { FaShip, FaEnvelope, FaPhone, FaMapMarkerAlt, FaFacebook, FaTwitter, FaLinkedin, FaInstagram } from 'react-icons/fa';

const FooterContainer = styled.footer`
  background: ${theme.colors.background.primary};
  border-top: 1px solid ${theme.colors.border.primary};
  padding: ${theme.spacing['2xl']} 0 ${theme.spacing.lg} 0;
  margin-top: auto;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.lg};
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FooterTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const FooterText = styled.p`
  color: ${theme.colors.text.secondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: ${theme.spacing.sm};
`;

const FooterLink = styled.a`
  color: ${theme.colors.text.secondary};
  text-decoration: none;
  transition: ${theme.transitions.colors};
  margin-bottom: ${theme.spacing.xs};
  display: block;
  
  &:hover {
    color: ${theme.colors.primary.light};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.md};
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: ${theme.borderRadius.full};
  color: ${theme.colors.primary.light};
  text-decoration: none;
  transition: ${theme.transitions.default};
  
  &:hover {
    background: ${theme.colors.primary.main};
    color: ${theme.colors.primary.contrast};
    transform: translateY(-2px);
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${theme.colors.border.primary};
  padding-top: ${theme.spacing.xl};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: ${theme.colors.text.disabled};
  font-size: ${theme.typography.fontSize.sm};
  margin: 0;
`;

const PoweredBy = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.text.disabled};
  font-size: ${theme.typography.fontSize.sm};
`;

const ModernMarineLink = styled.a`
  color: ${theme.colors.primary.light};
  text-decoration: none;
  font-weight: ${theme.typography.fontWeight.semibold};
  
  &:hover {
    color: ${theme.colors.primary.main};
  }
`;

const Footer = () => {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <FooterSection>
            <FooterTitle>
              <FaShip style={{ marginRight: '8px', color: theme.colors.primary.light }} />
              BoatWise
            </FooterTitle>
            <FooterText>
              The next-generation marine monitoring platform that brings your vessel's data to life. 
              Monitor, analyze, and optimize your boat's performance with real-time insights.
            </FooterText>
            <SocialLinks>
              <SocialLink
                href="https://facebook.com/modernmarine" 
                target="_blank" 
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaFacebook />
              </SocialLink>
              <SocialLink
                href="https://twitter.com/modernmarine" 
                target="_blank" 
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaTwitter />
              </SocialLink>
              <SocialLink
                href="https://linkedin.com/company/modernmarine" 
                target="_blank" 
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaLinkedin />
              </SocialLink>
              <SocialLink
                href="https://instagram.com/modernmarine" 
                target="_blank" 
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaInstagram />
              </SocialLink>
            </SocialLinks>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Features</FooterTitle>
            <FooterLink href="/features">Real-Time Monitoring</FooterLink>
            <FooterLink href="/features">Electrical Systems</FooterLink>
            <FooterLink href="/features">Navigation & Tracking</FooterLink>
            <FooterLink href="/features">Safety & Security</FooterLink>
            <FooterLink href="/features">Mobile App</FooterLink>
            <FooterLink href="/features">Data Analytics</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Company</FooterTitle>
            <FooterLink href="/about">About Us</FooterLink>
            <FooterLink href="/contact">Contact</FooterLink>
            <FooterLink href="/privacy">Privacy Policy</FooterLink>
            <FooterLink href="/terms">Terms of Service</FooterLink>
            <FooterLink href="/support">Support</FooterLink>
            <FooterLink href="/careers">Careers</FooterLink>
          </FooterSection>

          <FooterSection>
            <FooterTitle>Contact Info</FooterTitle>
            <FooterText>
              <FaMapMarkerAlt style={{ marginRight: '8px', color: theme.colors.primary.light }} />
              Port Perry, ON<br />
              We travel with our portable workshop!
            </FooterText>
            <FooterText>
              <FaPhone style={{ marginRight: '8px', color: theme.colors.primary.light }} />
              416-570-6895
            </FooterText>
            <FooterText>
              <FaEnvelope style={{ marginRight: '8px', color: theme.colors.primary.light }} />
              boatwise@modernmarine.ca
            </FooterText>
          </FooterSection>
        </FooterGrid>

        <FooterBottom>
          <Copyright>
            © 2024 BoatWise. All rights reserved.
          </Copyright>
          <PoweredBy>
            Powered by{' '}
            <ModernMarineLink href="https://www.modernmarine.ca" target="_blank" rel="noopener noreferrer">
              Modern Marine
            </ModernMarineLink>
          </PoweredBy>
        </FooterBottom>
      </FooterContent>
    </FooterContainer>
  );
};

export default Footer;
